/**
 * @module "Free Movie of the Day" List
 * @author Jacob Viertel <jv@onscreen.net>
 */

import {AttributeLink, AttributeSetup} from '@acng/frontend-stargazer';
import {Watch} from '@acng/frontend-relativity';
import {size} from '@acng/frontend-bounty/std/array.js';
import {now} from '@acng/frontend-bounty/std/date.js';
import {max} from '@acng/frontend-bounty/std/number.js';

import {Actor, ctxList} from '../../layout/context/list.js';
import {ctxFreeMovieList} from '../context/list.js';
import {compile, ListItem} from '../service/template.js';

/**
 * Provide a freemovie list as {@link ctxList} to the DOM subtree
 * or remove it if the FMOTD feature is hidden.
 *
 * @example
 * ```html
 * <!-- Display upcoming movies -->
 * <nova-list fmotd-list class="warp-grid">
 *   ${nickname} - ${title}
 * </nova-list>
 *
 * <!-- Display past movies -->
 * <nova-list fmotd-list="past" class="warp-grid">
 *   ${nickname} - ${title}
 * </nova-list>
 * ```
 *
 * @group DOM Attribute
 */
export const FMOTD_LIST = 'fmotd-list';

AttributeSetup(FMOTD_LIST, (name) => {
  const watchList = Watch(`[${name}]`, ctxFreeMovieList);
  const provideFiltered = Actor();

  /**
   * @param {import('../context/list.js').FreeMovieData[]} freemovies
   * @param {boolean} selectPast
   */
  const filter = (freemovies, selectPast) => {
    const currentTime = now();

    let pivot = -1;

    for (let i = 0, l = size(freemovies); i < l; i++) {
      if (freemovies[i].free_since <= currentTime) {
        pivot = i;
        break;
      }
    }

    return selectPast
      ? freemovies.slice(pivot + 1)
      : freemovies.slice(max(0, pivot - 2), pivot + 1).reverse();
  };

  AttributeLink(name, (element, value) => {
    watchList(element, (element, freemovies) => {
      /**
       * @param {number} offset
       */
      const more = async (offset) => (offset ? [] : filter(freemovies, value == 'past'));

      provideFiltered(element, more, ListItem);
    });
  });

  return compile;
});
